// Every status label and antd Tag colour map of the admin panel, in one place.
import type { BookingUpdateBody } from '../api/schemas';
import type {
  AdminBookingPayment,
  BookingStatus,
  CharterBookingStatus,
  CharterQuote,
  CharterRequestStatus,
} from '../api/types';

// ─── ShareJet bookings ──────────────────────────────────────────────────

// SHAR-558 (client, 17 Aug) — DISPLAY NAMES ONLY. The stored values, the API
// and every rule that reads them are unchanged: `pending` and `pre_approved` are
// still the statuses in the database and on the wire, they are simply written
// differently for people. Renaming the underlying values would have meant a
// migration and would have broken the mobile builds already in the field.
//
// "Pending" and "Pre-approved" described the ADMIN's position (are we happy to
// go ahead) rather than what is actually happening to the flight, which is what
// a customer and an operator care about.
export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  pending: 'Gathering passengers',
  pre_approved: 'Under final review',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  complete: 'Completed',
};

// antd Tag colours per status.
export const BOOKING_STATUS_COLORS: Record<BookingStatus, string> = {
  pending: 'gold',
  pre_approved: 'geekblue',
  confirmed: 'green',
  cancelled: 'red',
  complete: 'default',
};

// A payment attempt on a ShareJet booking.
export const PAYMENT_STATUS_COLORS: Record<AdminBookingPayment['status'], string> = {
  authorized: 'blue',
  captured: 'green',
  failed: 'red',
  released: 'default',
  captured_hold: 'green',
  voided: 'default',
  refunded: 'orange', // SHAR-107
  expired: 'volcano', // SHAR-157 — the hold lapsed before it was captured
};

/**
 * What a cancellation left on a payment (SHAR-1171). Text on tag background,
 * WCAG contrast measured on antd's preset (colour-7 on colour-1): purple 8.88:1,
 * red 5.07:1. Orange (3.34:1) fails AA at tag size; see
 * docs/design/admin-cancellation-money-tags.md.
 */
export const CANCELLATION_MARK_COLORS = {
  refundOutsideApp: 'purple',
  releaseFailed: 'red',
} as const;

/** What became of a seat-holder's money at a cancellation (the "At cancellation" column, SHAR-1091). */
export const CANCELLATION_FATE_COLORS = {
  captured: 'green',
  // the same colour as the "Release failed" mark it goes with
  stillHeld: CANCELLATION_MARK_COLORS.releaseFailed,
  expired: 'red',
  released: 'default',
  nothingHeld: 'default',
  refunded: 'orange',
} as const;

/** How a payment status reads; the API's word where it reads well already. */
export const PAYMENT_STATUS_LABELS: Partial<Record<AdminBookingPayment['status'], string>> = {
  captured_hold: 'captured from hold',
};

// ─── Charter requests ───────────────────────────────────────────────────

// Charter REQUEST statuses. Deliberately separate from the booking status map:
// they are a different set of states, and the client asked to keep them apart.
export const CHARTER_REQUEST_STATUS_LABELS: Record<CharterRequestStatus, string> = {
  pending: 'Pending',
  quotes_received: 'Quotes received',
  booked: 'Booked',
  cancelled: 'Cancelled',
};
export const CHARTER_REQUEST_STATUS_COLORS: Record<CharterRequestStatus, string> = {
  pending: 'gold',
  quotes_received: 'geekblue',
  booked: 'green',
  cancelled: 'red',
};

// The charter requests list's ad-hoc label cards and tags (SHAR-1105/1116).
export type CharterRequestAdhocFilter = 'paymentRequired' | 'quoteRefresh';
export const CHARTER_REQUEST_ADHOC_COLORS: Record<CharterRequestAdhocFilter, string> = { paymentRequired: 'red', quoteRefresh: 'gold' };

export const CHARTER_QUOTE_STATUS_COLORS: Record<CharterQuote['status'], string> = {
  created: 'default',
  pending: 'geekblue',
  expired: 'orange',
  booked: 'green',
  declined: 'red',
  // SHAR-813 — the quote a cancelled booking was made from. Distinct from
  // `declined`, which says the customer turned the offer down; this one they
  // accepted and paid for. Volcano rather than red so the two do not read as
  // the same outcome at a glance.
  cancelled: 'volcano',
};

// ─── Charter bookings ───────────────────────────────────────────────────

// Charter BOOKING statuses. A separate set from charter request statuses and
// from ShareJet booking statuses, kept apart on purpose as the others are.
export const CHARTER_BOOKING_STATUS_LABELS: Record<CharterBookingStatus, string> = {
  pre_approved: 'Pre-approved',
  confirmed: 'Confirmed',
  complete: 'Completed',
  cancelled: 'Cancelled',
};
export const CHARTER_BOOKING_STATUS_COLORS: Record<CharterBookingStatus, string> = {
  pre_approved: 'gold',
  confirmed: 'green',
  complete: 'blue',
  cancelled: 'red',
};

// ─── The API's label (SHAR-1184) ─────────────────────────────────────

/** "quotes_received" -> "Quotes received": what the API itself does for a value it does not know. */
export function statusInWords(value: string | null | undefined): string {
  const w = (value ?? '').trim().replace(/[_-]+/g, ' ').toLowerCase();
  return w ? w[0].toUpperCase() + w.slice(1) : '';
}

/**
 * SHAR-1184 — how a status reads: the API's `statusLabel` (one wording for the
 * apps and the admin, api/src/common/status-labels.ts), else this file's own
 * label (an API before SHAR-1184, today's staging), else the value in words.
 * The tables above stay as that fallback and for the filters and pickers, which
 * name values before any row has been loaded.
 */
export function statusLabel<S extends string>(
  labels: Partial<Record<S, string>>,
  status: S | null | undefined,
  apiLabel?: string | null,
  /** What a value the table lacks reads as; the value in words unless given. */
  otherwise?: string,
): string {
  const l = apiLabel?.trim();
  if (l) return l;
  if (status == null) return '—';
  return labels[status] ?? otherwise ?? statusInWords(status);
}

// Charter QUOTE statuses. The quotes table printed the raw value ("pending");
// these are the API's words (docs/statuses.md), for an API without labels.
export const CHARTER_QUOTE_STATUS_LABELS: Record<CharterQuote['status'], string> = {
  created: 'Draft',
  pending: 'Pending',
  expired: 'Expired',
  booked: 'Booked',
  declined: 'Declined',
  cancelled: 'Cancelled',
};

// ─── What the admin may send (SHAR-1211) ────────────────────────────────

/**
 * The status a booking edit may send. `complete` is derived (confirmed and
 * landed, SHAR-413) and the API's AdminUpdateBookingDto refuses it, so saving
 * the flight ID of a completed booking used to fail with a 400. Unchanged, it
 * is simply not sent.
 */
export function settableBookingStatus(status: BookingStatus): BookingUpdateBody['status'] {
  return status === 'complete' ? undefined : status;
}
